import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { GetCallsResponse } from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { UsageType } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/phone_number.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { InfiniteData, useQueryClient } from 'react-query';
import { CallIntelligenceApi } from '@frontend/api-analytics';
import { CallIntelTypes, CallIntelligenceV2 } from '@frontend/api-call-intel';
import { LiteApi } from '@frontend/api-lite';
import { CallInsightAccordionType, PanelEvents, useCallInsightController } from '@frontend/call-insight-panel';
import { callStatusUtils, generateTrackingId, useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore, useScopedQuery } from '@frontend/scope';
import { useContactPanelStore, useIsSmallMode } from '@frontend/shared';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import {
  DocumentErrorIcon,
  DocumentFilledIcon,
  DocumentUndeterminedIcon,
  DocumentWarningIcon,
  SortingValue,
  SpinningLoader,
  Text,
  emptyStateGraphics,
  useModalControl,
} from '@frontend/design-system';
import { featureFlags } from '../../../feature-flags';
import { queryKeys } from '../../../query-keys';
import { trackingIds } from '../../../tracking-ids';
import { callIntelligenceUtils, getApiSearchQuery } from '../../../utils/call-intelligence';
import { CallRecordsExportModal } from '../call-records-export-modal';
import { SearchResultSummary } from '../filters/molecules/search-result-summary';
import {
  CallIntelRecordsFilters,
  type CallIntelRecordsFiltersProps,
} from '../filters/organisms/call-intel-records-filters';
import { hasCallIntelAnalysisFilters } from '../filters/utils';
import { useActiveFilters } from '../hooks/use-active-filters';
import { useCallIntelDemoFlags } from '../hooks/use-call-intel-demo-flags';
import { useCallIntelLocations } from '../hooks/use-call-intel-locations';
import { useCallIntelShallowStore } from '../hooks/use-call-intel-store';
import { useCallRecordsColumns } from '../hooks/use-call-records-column';
import { useCallRecordsData } from '../hooks/use-call-records-data';
import { useCallRecordsSearchHandlers } from '../hooks/use-call-records-search-handlers';
import { useCallRecordsWebsocketSubscription } from '../hooks/use-call-records-websocket-subscription';
import { useOfficeUsers } from '../hooks/use-office-users';
import { useCallIntelTranscriptSearchHistory } from '../hooks/use-transcript-search-history';
import { useVoiceAiLocations } from '../hooks/use-voice-ai-locations';
import { CallRecordsMobileView } from '../table-views/call-records-mobile-view';
import { CallRecordsTableView } from '../table-views/call-records-table-view';

const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_TABLE_SORT_TYPE = CallIntelTypes.SortType.SORT_DESC;
const MIN_SEARCH_LENGTH = 3;
const DEFAULT_SEARCH_TYPE = CallIntelTypes.SearchTypeEnum.SEARCH_BY_CONTENT;

type EmptyStateIconType = keyof typeof emptyStateGraphics;

type GetEmptyStateConfigParams = {
  drillDownFilters?: CallIntelTypes.DrillDownOptions;
  isSmallMode?: boolean;
  isTranscriptSearchEnabled?: boolean;
  searchQuery: CallIntelTypes.SearchState;
};

type CallRecordsTableProps = {
  customToolbar?: React.ReactNode;
  defaultAccordion?: CallInsightAccordionType;
  drillDownFilters?: CallIntelTypes.DrillDownOptions;
  hideColumns?: string[];
  isDemoAccount?: boolean;
  marginTop?: number;
  mobileViewHeight?: number | string;
  priorityColumns?: string[];
} & Pick<CallIntelRecordsFiltersProps, 'hideChipFilters' | 'hideFilters' | 'showOnlyTrayFilters'>;

const getTrayIcon = (call?: CallIntelTypes.CallRecord, isDemoAccount?: boolean, isEdited?: boolean) => {
  if (!call?.status) return;
  const { isProcessingCall, isFailedCall, isSkippedCall, isPoorAudio } = callStatusUtils.getCallFlags(call);
  if (isEdited) return <Icon name='document-sent' color='success' size={24} />;
  if (isProcessingCall) return <SpinningLoader size='small' style={{ marginLeft: theme.spacing(-0.5) }} />;
  if (isFailedCall) return <DocumentErrorIcon color='error' />;
  if (isSkippedCall) return <DocumentUndeterminedIcon color='subdued' />;
  if (!isDemoAccount && isPoorAudio) return <DocumentWarningIcon color='warn' />;
  return (
    <span>
      <DocumentFilledIcon color='light' />
    </span>
  );
};

// Function to generate empty state configurations
const getEmptyStateConfig = ({
  drillDownFilters,
  searchQuery,
  isSmallMode,
  isTranscriptSearchEnabled,
}: GetEmptyStateConfigParams) => {
  if (searchQuery.value.length) {
    const emptyDescription = isTranscriptSearchEnabled
      ? i18next.t('Try a different phrase or adjusting your search.', { ns: 'call-intelligence' })
      : i18next.t('Try adjusting your filters or searching for a different name or number.', {
          ns: 'call-intelligence',
        });

    if (isSmallMode) {
      return {
        type: 'summary' as EmptyStateIconType,
        header: i18next.t('No results that match your search.', { ns: 'call-intelligence' }),
        description: emptyDescription,
      };
    }

    return {
      type: 'summary' as EmptyStateIconType,
      header: '',
      description: () => (
        <div css={styles.emptyStateWrapper}>
          <Text css={styles.emptyStateText}>
            {i18next.t('No results that match your search.', { ns: 'call-intelligence' })}
          </Text>
          <Text css={styles.emptyStateText}>{emptyDescription}</Text>
        </div>
      ),
    };
  }
  if (drillDownFilters?.key === 'service_quality_flags') {
    if (isSmallMode) {
      return {
        type: 'phone_data' as EmptyStateIconType,
        header: i18next.t('No data to display.', { ns: 'call-intelligence' }),
        description: '',
      };
    }
    return {
      type: 'phone_data' as EmptyStateIconType,
      header: '',
      description: () => (
        <div css={styles.emptyStateWrapper}>
          <Text css={styles.emptyStateText}>{i18next.t('No data to display.', { ns: 'call-intelligence' })}</Text>
        </div>
      ),
    };
  }
  // Default empty state when no specific condition is met
  return {
    type: 'sync_your_phone' as EmptyStateIconType,
  };
};

export const CallRecordsTable = ({
  customToolbar,
  defaultAccordion = 'callIntel',
  drillDownFilters,
  hideChipFilters,
  hideColumns = [],
  hideFilters,
  isDemoAccount,
  marginTop = 40,
  mobileViewHeight,
  priorityColumns = [],
  showOnlyTrayFilters,
}: CallRecordsTableProps) => {
  const queryClient = useQueryClient();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { isSmallMode } = useIsSmallMode();

  const { filters } = useActiveFilters();
  const { isDemoModal } = useCallIntelDemoFlags();
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<CallIntelTypes.SearchState>({
    query: '',
    value: '',
    searchType: DEFAULT_SEARCH_TYPE,
  });
  const [isTranscriptSearchActive, setIsTranscriptSearchActive] = useState(false);
  const [editedCallIds, setEditedCallIds] = useState<string[]>([]); //TODO:set edited call ids by listening to call insight panel events
  const { isMultiLocation, locations, locationIds } = useCallIntelLocations();
  const { subView } = useCallIntelShallowStore('subView');

  const { personId } = useContactPanelStore();
  const downloadBtnRef = useRef<HTMLButtonElement>(null);

  const isAutoRefreshRecordsEnabled = getFeatureFlagValue(featureFlags.autoRefreshCallRecords);
  const isCallIntelCsvExportEnabled = getFeatureFlagValue(featureFlags.enableCallIntelCsvExport);
  const isServiceQualityEnabled = getFeatureFlagValue(featureFlags.enableServiceQuality);
  const isTranscriptSearchEnabled = getFeatureFlagValue(featureFlags.enableTranscriptSearch);

  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);
  const hasDataExportAccess = useHasCallIntelAclAccess([Permission.CALL_CLASSIFICATION_READ], {
    locations: locationIds,
    allowWeaveOverride: true,
    allowDemoOverride: false,
  });
  const { isTaskCreationEnabled: isTasksEnabled, locationTaskSettingsMap } =
    CallIntelligenceV2.Hooks.useAggregateTaskSettings({
      locationIds,
      checkTaskCategoriesAggregate: true,
      isDemo: isDemoAccount,
    });

  const { activeLocationIds: activeVoiceAiLocations } = useVoiceAiLocations({ locationIds: filters.locations ?? [] });
  const downloadModalControls = useModalControl();

  const {
    activeCall,
    clearCallList,
    emitEvent,
    onEvent,
    offEvent,
    open: openCallInsightPanel,
    setCallList,
    setIsDemoAccount,
  } = useCallInsightController({
    source: 'callIntel',
  });

  const selectedCall = activeCall?.callIntelInsights;

  const [tableState, setTableState] = useState({
    pageConfig: { pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE },
    sortType: DEFAULT_TABLE_SORT_TYPE,
  });

  const pageIndex = tableState.pageConfig.pageNumber - 1;

  const emptyStateConfig = useMemo(
    () => getEmptyStateConfig({ drillDownFilters, searchQuery, isSmallMode, isTranscriptSearchEnabled }),
    [searchQuery, drillDownFilters, isSmallMode, isTranscriptSearchEnabled]
  );

  const privateCommBannerSource = useMemo(
    () => callIntelligenceUtils.getPrivateCommsBannerSource(drillDownFilters),
    [drillDownFilters]
  );

  // Office numbers query
  const { data: officeNumbers } = useScopedQuery({
    queryKey: queryKeys.callIntelligence(
      `office-numbers-${isDemoAccount}-${isMounted}-${JSON.stringify(filters.locations)}`
    ),
    queryFn: () => LiteApi.getPhoneNumbers((filters?.locations ?? []).join('&location_ids=')),
    onError: (error) => {
      console.error(`Failed to get the phone numbers, ${error}`);
    },
    enabled: !!(isMounted && filters.locations?.length),
    refetchOnWindowFocus: false,
    retry: false,
    select: (data) => {
      // Only return phone numbers for voice and sms and non e911 usage type

      return (
        data?.phoneNumbers
          .filter(
            ({ number }) =>
              (number.voiceProvider || number.smsProvider) && number.phoneNumber.usageType !== UsageType.USAGE_TYPE_E911
          )
          .map(({ number }) => `${number.phoneNumber.phoneNumber.nationalNumber}`) || []
      );
    },
    staleTime: 1000 * 60 * 5,
  });

  const { activeOfficeUsersForFilter } = useOfficeUsers({
    isDemoLocation: filters.locations?.[0]?.includes('demo'),
    locationIds: filters.locations || [],
  });

  const apiSearchQuery = useMemo(() => getApiSearchQuery(searchQuery), [searchQuery]);

  const { listCallsQuery: infiniteQueryProps, listCallsPayload } = useCallRecordsData({
    accessConfig: {
      isServiceQualityEnabled: isServiceQualityEnabled,
      hasServiceQualityAccess: hasServiceQualityAccess,
    },
    drillDownFilters,
    searchQuery: apiSearchQuery,
    tableState,
    isMounted,
  });

  const { data, fetchNextPage, hasNextPage, hasPreviousPage, isFetching } = infiniteQueryProps;

  useCallRecordsWebsocketSubscription({
    enabled: isMounted && isAutoRefreshRecordsEnabled,
    activeQueryKey: infiniteQueryProps.queryKey,
    isInfiniteList: isSmallMode,
    currentPage: tableState.pageConfig.pageNumber,
    pageSize: tableState.pageConfig.pageSize,
    isCallIntelAnalysisFiltersApplied: hasCallIntelAnalysisFilters(filters),
    listCallsPayload,
    onTagUpdate: (channelId, tagIds) => updateCallTagsInCache({ id: channelId, tagIds }),
  });

  const calls = useMemo<CallIntelTypes.CallRecord[]>(() => {
    if (!data) return [];
    if (isDemoAccount) {
      return data?.pages[0]?.rows ?? [];
    }
    if (isSmallMode) {
      return data.pages.flatMap((page) => page.rows ?? []);
    }
    const _pageIndex = tableState.pageConfig.pageNumber - 1;
    return data.pages[_pageIndex]?.rows ?? [];
  }, [data, isDemoAccount, isSmallMode, tableState.pageConfig.pageNumber]);

  // Calls stats query
  const { data: callsStats } = useScopedQuery({
    queryKey: queryKeys.callIntelligence(
      `calls-stats-${JSON.stringify(
        Object.keys(filters)
          .filter((key) => key !== 'hideNonAnalyzedCalls')
          .map((key) => filters[key as keyof CallIntelTypes.Filters])
      )}-${apiSearchQuery.search}-${apiSearchQuery.searchType}-${isMounted}`
    ),
    queryFn: () =>
      CallIntelligenceApi.getCallsStats({
        filters,
        searchQuery: apiSearchQuery,
      }),
    enabled: isMounted && !drillDownFilters,
    onError: (error) => {
      console.error('Failed to fetch calls stats', error);
    },
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { registerTranscriptSearchClick, resetTranscriptSearchHistory, startTranscriptSearchHistory } =
    useCallIntelTranscriptSearchHistory({
      calls,
      callsQueryKey: infiniteQueryProps.queryKey,
      isFetching,
      isSmallMode,
      isTranscriptSearchEnabled,
      locationIds: filters.locations ?? [],
      minSearchLength: MIN_SEARCH_LENGTH,
      searchQuery,
      tableState,
    });

  const handleSorting = useCallback((sortValues: SortingValue<string>[]) => {
    const { id, value } = sortValues[0] || {};
    if (id === 'time') {
      setTableState((prev) => ({
        ...prev,
        sortType: value === 'asc' ? CallIntelTypes.SortType.SORT_ASC : CallIntelTypes.SortType.SORT_DESC,
        pageConfig: { ...prev.pageConfig, pageNumber: 1 },
      }));

      pendo.track(generateTrackingId({ component: 'call-records-table', context: 'time-sort' }));
    }
  }, []);

  const handlePaginationAfterApplyingFilters = useCallback((sortType?: CallIntelTypes.SortType) => {
    setTableState((prev) => ({
      ...prev,
      ...(sortType && { sortType }),
      pageConfig: { ...prev.pageConfig, pageNumber: 1 },
    }));
  }, []);

  const { handleSearchOnChange, handleSearchTypeChange, hasTranscriptSearchQuery } = useCallRecordsSearchHandlers({
    isFetching,
    isTranscriptSearchEnabled,
    searchQuery,
    setSearchQuery,
    minSearchLength: MIN_SEARCH_LENGTH,
    defaultSearchType: DEFAULT_SEARCH_TYPE,
    defaultTableSortType: DEFAULT_TABLE_SORT_TYPE,
    onPaginationAfterApplyingFilters: handlePaginationAfterApplyingFilters,
    resetTranscriptSearchHistory,
    startTranscriptSearchHistory,
  });

  const updateCallsInCache = useCallback(
    (call: Partial<Call>) => {
      queryClient.setQueryData<InfiniteData<GetCallsResponse> | undefined>(infiniteQueryProps.queryKey, (oldData) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            newCalls: page.newCalls?.map((existingCall) =>
              existingCall.id === call.id ? { ...existingCall, ...call } : existingCall
            ),
          })),
        };
      });
    },
    [infiniteQueryProps.queryKey, queryClient]
  );

  const updateCallTagsInCache = useCallback(
    (call: CallIntelTypes.CallRecord) => {
      updateCallsInCache(call);
      if (call.id) {
        emitEvent('callTagsExternalSync', { channelId: call.id, tagIds: call.tagIds ?? [] });
      }
    },
    [emitEvent, updateCallsInCache]
  );

  const handleUpdateCallAnalysis = useCallback(
    ({ call }: PanelEvents['callAnalysisUpdated']) => {
      updateCallsInCache(call);

      setEditedCallIds((prevIds) => {
        const updatedIds = new Set(prevIds);
        call.id && updatedIds.add(call.id);
        return Array.from(updatedIds);
      });
    },
    [updateCallsInCache]
  );

  const handleDeleteTask = useCallback(
    ({ call }: PanelEvents['taskDeleted']) => {
      updateCallsInCache(call);
    },
    [updateCallsInCache]
  );

  const handleRowActionClick = useCallback(
    (rowData: CallIntelTypes.CallRecord) => {
      registerTranscriptSearchClick(rowData);
      if (rowData.id !== selectedCall?.id) {
        openCallInsightPanel(
          {
            channelId: rowData?.id ?? '',
            includeCallIntelligence: false,
            callIntelData: rowData,
            locationId: rowData?.locationId ?? '',
          },
          {
            defaultAccordion,
            suppressGlobalPanelOpen: isDemoModal,
            trackingContext: {
              source: 'call-records-table',
              searchQuery: searchQuery.query,
              isDrilldownPage: !!drillDownFilters,
            },
          }
        );
      }
    },
    [
      selectedCall?.id,
      registerTranscriptSearchClick,
      openCallInsightPanel,
      defaultAccordion,
      isDemoModal,
      searchQuery.query,
      drillDownFilters,
    ]
  );

  const handlePagination = useCallback((action: 'next' | 'prev') => {
    setTableState((prev) => ({
      ...prev,
      pageConfig: {
        ...prev.pageConfig,
        pageNumber: action === 'next' ? prev.pageConfig.pageNumber + 1 : prev.pageConfig.pageNumber - 1,
      },
    }));
  }, []);

  const handlePageSizeChange = useCallback((num: number) => {
    setTableState((prev) => ({
      ...prev,
      pageConfig: { pageNumber: 1, pageSize: num },
    }));
  }, []);

  const handleSortTypeChange = useCallback((sortType: CallIntelTypes.SortType) => {
    setTableState((prev) => ({
      ...prev,
      sortType,
      pageConfig: { ...prev.pageConfig, pageNumber: 1 },
    }));
  }, []);

  const handleExport = useCallback(() => {
    downloadModalControls.triggerProps.onClick();
  }, [downloadModalControls.triggerProps]);

  const { tableColumns, smallModeColumns } = useCallRecordsColumns({
    accessConfig: {
      isTasksEnabled,
      isServiceQualityEnabled,
      hasServiceQualityAccess,
    },
    dataConfig: {
      selectedCall,
      personId,
      isDemoModal,
      locations,
      isMultiLocation: !!isMultiLocation,
      activeVoiceAiLocations,
      locationTaskSettingsMap,
      officeNumbers,
      subView,
    },
    handlers: {
      openCallInsightPanel,
      updateCallTagsInCache,
    },
    displayConfig: {
      drillDownFilters,
    },
    filterConfig: {
      filters,
      hideColumns,
      priorityColumns,
    },
  });

  // Set all calls in store when calls change
  useEffect(() => {
    setCallList(
      calls.map((call) => ({
        channelId: call.id ?? '',
        locationId: call.locationId ?? '',
        includeCallIntelligence: false,
        callIntelData: call,
      })),
      {
        suppressGlobalPanelOpen: isDemoModal,
        trackingContext: {
          source: 'call-records-table',
          searchQuery: searchQuery.query,
          isDrilldownPage: !!drillDownFilters,
        },
      }
    );
    setIsDemoAccount(!!isDemoAccount);
  }, [calls, setCallList, isDemoAccount, isDemoModal, setIsDemoAccount, drillDownFilters, searchQuery.query]);

  // Reset page number when filters change
  useEffect(() => {
    setTableState((prev) => ({
      ...prev,
      pageConfig: { ...prev.pageConfig, pageNumber: 1 },
    }));
  }, [filters]);

  useEffect(() => {
    if (isDemoAccount) return;
    if (!data) return;

    const pageAlreadyLoaded = !!data.pages[pageIndex];

    if (!pageAlreadyLoaded && hasNextPage) {
      fetchNextPage();
    }
  }, [isDemoAccount, hasNextPage, data, fetchNextPage, pageIndex]);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    onEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
    onEvent('callTagsUpdated', (payload) => {
      updateCallsInCache({
        ...payload.callRecord.callIntelInsights,
        tagIds: payload.tagIds,
      });
    });
    onEvent('taskDeleted', handleDeleteTask);
    return () => {
      offEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
      offEvent('taskDeleted', handleDeleteTask);
      offEvent('callTagsUpdated', () => null);
    };
  }, [onEvent, offEvent, handleUpdateCallAnalysis, handleDeleteTask, updateCallsInCache]);

  useEffect(() => {
    return () => clearCallList();
  }, [clearCallList]);

  // Derived display flags
  const hasExportDataAccess =
    isCallIntelCsvExportEnabled && !drillDownFilters && (isDemoAccount || hasDataExportAccess);
  const shouldShowExportAction = hasExportDataAccess && !isTranscriptSearchActive;

  const totalRecordsCount = data?.pages?.[pageIndex]?.totalRecordsCount ?? 0;

  const renderSearchResultSummary = useMemo(() => {
    if (!hasTranscriptSearchQuery) return undefined;
    return (
      <SearchResultSummary
        resultCount={totalRecordsCount}
        sortType={tableState.sortType}
        onSortTypeChange={handleSortTypeChange}
      />
    );
  }, [hasTranscriptSearchQuery, totalRecordsCount, tableState.sortType, handleSortTypeChange]);

  const renderFilters = useMemo(() => {
    return (
      <CallIntelRecordsFilters
        isLoading={isFetching}
        onApplyingFilters={handlePaginationAfterApplyingFilters}
        officeNumbers={officeNumbers}
        officeUsers={activeOfficeUsersForFilter}
        nonAnalyzedCallsCount={callsStats?.count}
        hideFilters={hideFilters}
        hideChipFilters={hideChipFilters}
        searchAction={{
          show: true,
          searchValue: searchQuery.value,
          trackingId: trackingIds.callIntel.allCallRecordingsSearch,
          onChange: handleSearchOnChange,
        }}
        exportAction={{
          // Show only in small mode for list view
          show: hasExportDataAccess && isSmallMode,
          onClick: handleExport,
          trackingId: generateTrackingId({
            component: 'call-records-table',
            context: 'export-icon',
            isDemoAccount,
          }),
        }}
        showOnlyTrayFilters={showOnlyTrayFilters}
        transcriptSearchAction={{
          show: isTranscriptSearchEnabled,
          searchType: searchQuery.searchType,
          value: searchQuery.value,
          isActive: isTranscriptSearchActive,
          onActiveChange: setIsTranscriptSearchActive,
          onChange: handleSearchOnChange,
          onSearchTypeChange: handleSearchTypeChange,
          trackingId: trackingIds.callIntel.transcriptSearchInput,
        }}
      />
    );
  }, [
    isFetching,
    handlePaginationAfterApplyingFilters,
    officeNumbers,
    activeOfficeUsersForFilter,
    callsStats?.count,
    hideFilters,
    hideChipFilters,
    searchQuery.value,
    searchQuery.searchType,
    handleSearchOnChange,
    hasExportDataAccess,
    isSmallMode,
    handleExport,
    isDemoAccount,
    showOnlyTrayFilters,
    isTranscriptSearchEnabled,
    isTranscriptSearchActive,
    handleSearchTypeChange,
  ]);

  const hasPrivateCalls = !!data?.pages[pageIndex]?.hasPrivateCalls;

  return (
    <>
      {!isSmallMode ? (
        <>
          <CallRecordsTableView
            calls={calls}
            isLoading={isFetching}
            emptyStateConfig={emptyStateConfig}
            tableState={tableState}
            hasNextPage={isDemoAccount ? false : !!data?.pages?.[pageIndex]?.hasNextPage}
            hasPreviousPage={!!hasPreviousPage}
            tableColumns={tableColumns}
            dataConfig={{
              officeNumbers,
              editedCallIds,
              minSearchLength: MIN_SEARCH_LENGTH,
              isDemoAccount,
              hasPrivateCalls,
              privateCommBannerSource,
            }}
            panelConfig={{
              selectedCall,
            }}
            displayConfig={{
              customToolbar: customToolbar ?? renderFilters,
              searchResultSummary: renderSearchResultSummary,
              hideFilters,
              marginTop,
              drillDownFilters,
              exportBtnRef: downloadBtnRef,
              isTranscriptSearchActive,
              showExportAction: shouldShowExportAction,
            }}
            handlers={{
              onFiltersChange: handlePaginationAfterApplyingFilters,
              onSearchChange: handleSearchOnChange,
              onSorting: handleSorting,
              onPagination: handlePagination,
              onPageSizeChange: handlePageSizeChange,
              onExport: handleExport,
              onRowActionClick: handleRowActionClick,
            }}
            getTrayIcon={getTrayIcon}
          />
        </>
      ) : (
        <CallRecordsMobileView
          emptyStateConfig={emptyStateConfig}
          infiniteQueryProps={infiniteQueryProps}
          smallModeColumns={smallModeColumns}
          customToolbarRender={customToolbar ?? renderFilters}
          searchResultSummary={renderSearchResultSummary}
          handlers={{ onRowActionClick: handleRowActionClick }}
          dataConfig={{ isDemoAccount, editedCallIds, privateCommBannerSource }}
          height={mobileViewHeight}
        />
      )}
      <CallRecordsExportModal {...downloadModalControls.modalProps} />
    </>
  );
};

const styles = {
  emptyStateText: css`
    color: ${theme.colors.neutral50};
  `,

  emptyStateWrapper: css`
    width: 400px;
    text-align: center;
  `,
};
